import { createContext, useState, useEffect, useCallback, useRef } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../config/firebase-config";
import { onSnapshot, collection, query, where } from "firebase/firestore";
import {
  getUserContexts,
  getResellerSession,
} from "../utils/core/auth/getUserContexts";
import {
  getActiveUnitId,
  setActiveUnitId,
  clearActiveUnit,
} from "../utils/core/auth/activeUnitStorage";
import {
  getActiveProductApp,
  setActiveProductApp,
  clearActiveProduct,
} from "../utils/core/auth/activeProductStorage";
import {
  SESSION_PRODUCT_APPS,
  buildProductChoices,
  buildCompatibilityUser,
  getActiveContextId,
  getContextChoices,
  getOperationalContextKey,
  getSelectionParameter,
  isContextForProduct,
  normalizeSelectedContext,
} from "../utils/core/auth/sessionContext";
import { PERMISSION_DENIED_EVENT } from "../utils/core/handleCallableError";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeContext, setActiveContext] = useState(null);
  const [activeMembership, setActiveMembership] = useState(null);
  const [activeUnit, setActiveUnit] = useState(null);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionState, setSessionState] = useState("resolving");
  const [resolverError, setResolverError] = useState(null);
  const [contextVersion, setContextVersion] = useState(0);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const resolutionIdRef = useRef(0);
  // Aplicaciones que el backend denegó en esta sesión. Se consumen en la
  // siguiente resolución para no volver a entrar en un producto denegado
  // (evita bucles infinitos de carga tras un 403).
  const deniedAppsRef = useRef(new Set());

  const clearOperationalState = useCallback(
    (nextState = "resolving", { clearProducts = true } = {}) => {
      setCurrentUser(null);
      setActiveContext(null);
      setActiveMembership(null);
      setActiveUnit(null);
      setAvailableUnits([]);
      setAvailableGroups([]);
      if (clearProducts) setAvailableProducts([]);
      setSessionState(nextState);
      setContextVersion((version) => version + 1);
    },
    [],
  );

  const updateUserData = useCallback((newData) => {
    // Adaptador temporal para componentes legacy. Nunca amplía permisos.
    setCurrentUser((previous) =>
      previous
        ? {
            ...previous,
            ...newData,
            permissions: previous.permissions,
            grantablePermissions: previous.grantablePermissions,
            allowedApps: previous.allowedApps,
            activeApp: previous.activeApp,
            activeUnitId: previous.activeUnitId,
            activeGroupId: previous.activeGroupId,
            dealershipId: previous.dealershipId,
            groupId: previous.groupId,
            contextType: previous.contextType,
            role: previous.role,
            administrativeScope: previous.administrativeScope,
          }
        : previous,
    );
  }, []);

  const applyContext = useCallback(
    (user, context) => {
      if (!isContextForProduct(context, context?.activeApp)) {
        clearOperationalState(
          context?.sessionState === "error" ? "error" : "unauthorized",
        );
        return false;
      }


      const units = Array.isArray(context.availableUnits)
        ? context.availableUnits
        : [];
      const groups = Array.isArray(context.availableGroups)
        ? context.availableGroups
        : [];
      const activeId = getActiveContextId(context);
      const choices = getContextChoices(context);
      const selected = choices.find((choice) => choice.id === activeId) || null;

      setAvailableUnits(units);
      setAvailableGroups(groups);
      setActiveUnit(
        context.activeUnitId
          ? units.find((unit) => unit.id === context.activeUnitId) ||
              null
          : null,
      );
      setActiveMembership(
        context.contextType === "operating_unit" ? selected : null,
      );
      setActiveContext(context);
      setCurrentUser(buildCompatibilityUser(user, context));
      setResolverError(null);
      setSessionState(context.sessionState);
      setContextVersion((version) => version + 1);
      return true;
    },
    [clearOperationalState],
  );

  const resolveContextForApp = useCallback(async (user, requestedApp) => {
    const initial =
      requestedApp === "reseller_app"
        ? await getResellerSession()
        : await getUserContexts({ requestedApp });
    if (!isContextForProduct(initial, requestedApp)) return null;

    // Reventa no tiene unidad/grupo operativo seleccionable.
    if (requestedApp === "reseller_app") return initial;

    const choices = getContextChoices(initial);
    const persistedId = getActiveUnitId(user.uid, requestedApp);
    if (!persistedId) return initial;

    const persistedChoice = choices.find((choice) => choice.id === persistedId);
    if (!persistedChoice) {
      // WEB-003: una selección revocada/inexistente nunca se conserva.
      clearActiveUnit(user.uid, requestedApp);
      return initial;
    }

    const selected = await getUserContexts({
      requestedApp,
      ...getSelectionParameter(requestedApp, persistedId),
    });
    const normalized = normalizeSelectedContext(selected, persistedId);
    if (
      isContextForProduct(normalized, requestedApp) &&
      normalized.sessionState === "ready" &&
      getActiveContextId(normalized) === persistedId
    ) {
      return normalized;
    }

    // La selección persistida dejó de ser válida. Se conserva el producto,
    // pero el usuario debe volver a elegir un scope validado por backend.
    clearActiveUnit(user.uid, requestedApp);
    return initial;
  }, []);

  const resolveSession = useCallback(
    async (user) => {
      const resolutionId = ++resolutionIdRef.current;
      if (!user?.uid || !user?.email) {
        clearOperationalState("unauthorized");
        return;
      }

      setSessionState("resolving");
      setResolverError(null);
      // Se consumen las apps denegadas por el backend: una denegación no debe
      // volver a entrar en el mismo producto al re-resolver (bucle de 403).
      const deniedApps = deniedAppsRef.current;
      deniedAppsRef.current = new Set();
      // Se resuelven todos los productos. Una respuesta de una app jamás se
      // usa como fallback de otra y el selector sólo recibe metadatos mínimos.
      const results = await Promise.allSettled(
        SESSION_PRODUCT_APPS.map((requestedApp) =>
          resolveContextForApp(user, requestedApp),
        ),
      );

      if (resolutionId !== resolutionIdRef.current || auth.currentUser?.uid !== user.uid) return;

      const validContexts = results
        .filter((result) => result.status === "fulfilled" && result.value)
        .map((result) => result.value);
      const productChoices = buildProductChoices(validContexts);
      const errors = results
        .filter((result) => result.status === "rejected")
        .map((result) => result.reason);

      setAvailableProducts(productChoices);

      if (productChoices.length > 1) {
        // WEB-004: se restaura el último producto elegido (por UID) si sigue
        // disponible. El contexto ya fue revalidado por el backend en la
        // resolución paralela; nunca se infiere del storage ni del payload.
        const persistedApp = getActiveProductApp(user.uid);
        const persistedContext =
          persistedApp &&
          validContexts.find((context) => context.activeApp === persistedApp);
        if (
          persistedContext &&
          !deniedApps.has(persistedApp) &&
          isContextForProduct(persistedContext, persistedApp) &&
          applyContext(user, persistedContext)
        ) {
          return;
        }
        clearOperationalState("selecting_product", { clearProducts: false });
        return;
      }

      if (productChoices.length === 1) {
        const onlyContext = validContexts.find(
          (context) => context.activeApp === productChoices[0].app,
        );
        if (
          onlyContext &&
          !deniedApps.has(productChoices[0].app) &&
          applyContext(user, onlyContext)
        ) {
          return;
        }
        // El único producto quedó denegado por el backend: pantalla de error
        // con opciones de revalidar o cerrar sesión, nunca un bucle.
        setResolverError(
          "No tenés permisos para operar en esta sección de AutoApp. Podés revalidar tu acceso o cerrar sesión.",
        );
        clearOperationalState("error");
        return;
      }

      if (errors.length > 0) {
        const lastError = errors[errors.length - 1];
        console.error("No se pudo resolver la sesión web.", lastError);
        setResolverError(lastError?.message || "Error resolviendo la sesión");
        clearOperationalState("error");
      } else {
        clearOperationalState("unauthorized");
      }
    },
    [applyContext, clearOperationalState, resolveContextForApp],
  );

  useEffect(() => {
    let cancelled = false;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (cancelled) return;
      setLoading(true);
      setFirebaseUser(user || null);
      if (user) {
        await resolveSession(user);
      } else {
        resolutionIdRef.current += 1;
        clearOperationalState("resolving");
      }
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
      resolutionIdRef.current += 1;
      unsubscribe();
    };
  }, [clearOperationalState, resolveSession]);

  const retrySession = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      clearOperationalState("unauthorized");
      return;
    }
    setLoading(true);
    clearOperationalState("resolving");
    await resolveSession(user);
    setLoading(false);
  }, [clearOperationalState, resolveSession]);

  const invalidateOperationalContext = useCallback(async () => {
    const user = auth.currentUser;
    const app = activeContext?.activeApp;
    if (user?.uid && app) clearActiveUnit(user.uid, app);
    await retrySession();
  }, [activeContext?.activeApp, retrySession]);

  useEffect(() => {
    const handlePermissionDenied = () => {
      const user = auth.currentUser;
      const deniedApp = activeContext?.activeApp;
      if (user?.uid) clearActiveProduct(user.uid);
      if (deniedApp) deniedAppsRef.current.add(deniedApp);
      setResolverError(
        "No tenés permisos para operar en este espacio. Podés revalidar tu acceso, cambiar de usuario o cerrar sesión.",
      );
      invalidateOperationalContext();
    };
    window.addEventListener(PERMISSION_DENIED_EVENT, handlePermissionDenied);
    return () =>
      window.removeEventListener(
        PERMISSION_DENIED_EVENT,
        handlePermissionDenied,
      );
  }, [invalidateOperationalContext, activeContext?.activeApp]);

  // Realtime de los PROPIOs bans del reventa (event-driven, sin polling):
  // cuando el admin banea/desbanea, el snapshot actualiza `bannedScopes` al
  // instante y el efecto del feed re-filtra (mismo mecanismo que la mobile).
  useEffect(() => {
    const isReseller =
      currentUser?.activeApp === "reseller_app" ||
      activeContext?.activeApp === "reseller_app";
    const uid = currentUser?.uid;
    if (!isReseller || !uid || sessionState !== "ready") return undefined;

    const bannedQuery = query(
      collection(db, "reseller_bans"),
      where("resellerUid", "==", uid),
    );
    const unsubscribe = onSnapshot(
      bannedQuery,
      (snapshot) => {
        const bannedScopes = snapshot.docs
          .map((doc) => doc.data())
          .filter((ban) => ban.status === "active")
          .map((ban) => String(ban.scope || ""))
          .filter((scope) => /^(unit|dealership|group):/.test(scope));
        setCurrentUser((previous) =>
          previous && previous.uid === uid
            ? { ...previous, bannedScopes }
            : previous,
        );
      },
      (error) => console.warn("[bans] error de suscripción:", error?.message),
    );
    return unsubscribe;
  }, [currentUser?.uid, currentUser?.activeApp, activeContext?.activeApp, sessionState]);

  const signOut = useCallback(async () => {
    resolutionIdRef.current += 1;
    deniedAppsRef.current = new Set();
    clearOperationalState("resolving");
    await auth.signOut();
  }, [clearOperationalState]);

  const beginUnitSelection = useCallback(() => {
    if (getContextChoices(activeContext).length < 2) return;
    resolutionIdRef.current += 1;
    setSessionState("selecting");
    setCurrentUser((previous) =>
      previous
        ? {
            ...previous,
            sessionState: "selecting",
            activeUnitId: null,
            activeGroupId: null,
            permissions: [],
            grantablePermissions: [],
          }
        : previous,
    );
    setContextVersion((version) => version + 1);
  }, [activeContext]);

  const beginProductSelection = useCallback(() => {
    if (availableProducts.length < 2) return;
    // Invalida requests del scope anterior y elimina permisos antes de mostrar
    // el selector; las opciones de producto no contienen permisos ni scopes.
    resolutionIdRef.current += 1;
    setResolverError(null);
    clearOperationalState("selecting_product", { clearProducts: false });
  }, [availableProducts.length, clearOperationalState]);

  const selectProduct = useCallback(
    async (requestedApp) => {
      const user = auth.currentUser;
      const choiceExists = availableProducts.some(
        (choice) => choice.app === requestedApp,
      );
      if (!user?.uid || !choiceExists) {
        throw new Error("El producto seleccionado ya no está disponible.");
      }

      const resolutionId = ++resolutionIdRef.current;
      setResolverError(null);
      // La elección explícita limpia la denegación previa de este producto.
      deniedAppsRef.current.delete(requestedApp);
      // No se confía en el contexto descubierto: se vuelve a consultar al
      // backend y sólo esa respuesta puede restaurar permisos y scope.
      clearOperationalState("selecting_product", { clearProducts: false });

      try {
        const context = await resolveContextForApp(user, requestedApp);
        if (resolutionId !== resolutionIdRef.current || auth.currentUser?.uid !== user.uid) return false;
        if (!isContextForProduct(context, requestedApp)) {
          throw new Error("El backend no pudo revalidar el producto elegido.");
        }
        if (!applyContext(user, context)) {
          throw new Error("El producto elegido ya no está habilitado.");
        }
        setActiveProductApp(user.uid, requestedApp);
        return true;
      } catch (error) {
        if (resolutionId !== resolutionIdRef.current || auth.currentUser?.uid !== user.uid) return false;
        clearActiveUnit(user.uid, requestedApp);
        setResolverError(error?.message || "Error revalidando el producto");
        clearOperationalState("selecting_product", { clearProducts: false });
        throw error;
      }
    },
    [
      applyContext,
      availableProducts,
      clearOperationalState,
      resolveContextForApp,
    ],
  );

  const selectUnit = useCallback(
    async (selectedId) => {
      const user = auth.currentUser;
      const activeApp = activeContext?.activeApp;
      const choiceExists = getContextChoices(activeContext).some(
        (choice) => choice.id === selectedId,
      );
      if (!user?.uid || !activeApp || !choiceExists) {
        throw new Error("La sucursal seleccionada ya no está disponible.");
      }

      const resolutionId = ++resolutionIdRef.current;
      const isCurrentSelection = () =>
        resolutionId === resolutionIdRef.current && auth.currentUser?.uid === user.uid;

      try {
        const response = await getUserContexts({
          requestedApp: activeApp,
          ...getSelectionParameter(activeApp, selectedId),
        });
        if (!isCurrentSelection()) return false;
        const context = normalizeSelectedContext(response, selectedId);
        if (
          !isContextForProduct(context, activeApp) ||
          context.sessionState !== "ready" ||
          getActiveContextId(context) !== selectedId
        ) {
          clearActiveUnit(user.uid, activeApp);
          throw new Error(
            "El backend no pudo revalidar la sucursal seleccionada.",
          );
        }

        setActiveUnitId(user.uid, activeApp, selectedId);
        applyContext(user, context);
        return true;
      } catch (error) {
        if (!isCurrentSelection()) return false;
        clearActiveUnit(user.uid, activeApp);
        if (String(error?.code || "").includes("permission-denied")) {
          await invalidateOperationalContext();
        }
        throw error;
      }
    },
    [activeContext, applyContext, invalidateOperationalContext],
  );

  // El último producto elegido se conserva en storage: volver a iniciar sesión
  // restaura directamente la última sesión sin pasar por el selector.
  const requestSignOut = useCallback(() => {
    if (availableProducts.length > 1) {
      setLogoutDialogOpen(true);
      return;
    }
    signOut();
  }, [availableProducts.length, signOut]);

  const confirmSignOut = useCallback(() => {
    setLogoutDialogOpen(false);
    signOut();
  }, [signOut]);

  const switchUserFromLogout = useCallback(() => {
    setLogoutDialogOpen(false);
    beginProductSelection();
  }, [beginProductSelection]);

  const cancelSignOutDialog = useCallback(() => {
    setLogoutDialogOpen(false);
  }, []);

  // Desde el selector de producto el cierre de sesión es directo y no conserva
  // el último producto: la próxima entrada vuelve a preguntar dónde ingresar.
  const signOutFromProductSelection = useCallback(async () => {
    const user = auth.currentUser;
    if (user?.uid) clearActiveProduct(user.uid);
    await signOut();
  }, [signOut]);

  const operationalContextKey = getOperationalContextKey(
    activeContext,
    contextVersion,
  );

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        currentUser,
        activeContext,
        activeMembership,
        activeUnit,
        availableUnits,
        availableGroups,
        availableProducts,
        availableWorkspaces: getContextChoices(activeContext),
        loading,
        sessionState,
        resolverError,
        operationalContextKey,
        signOut,
        retrySession,
        updateUserData,
        selectUnit,
        beginUnitSelection,
        selectProduct,
        beginProductSelection,
        invalidateOperationalContext,
        logoutDialogOpen,
        requestSignOut,
        confirmSignOut,
        switchUserFromLogout,
        cancelSignOutDialog,
        signOutFromProductSelection,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
